import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useOutletContext, useNavigate, useParams, useLocation } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import TaskSlideOver from './TaskSlideOver';
import PertDiagram from './red/PertDiagram';
import AnalisisPanel from './red/AnalisisPanel';

/**
 * Vista "Red" de un proyecto: diagrama PERT/CPM con la ruta crítica y, arriba,
 * el panel de análisis (números del cálculo + lectura de la IA).
 *
 * El cálculo vive en el backend (GET /api/proyectos/:id/ruta-critica) y se
 * vuelve a pedir cuando cambian las tareas del proyecto (por ejemplo al
 * cerrar una tarea desde el slide-over).
 */
const ProyectoRedView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { proyecto, tareas, refetchTareas, updateTareaLocal } = useOutletContext();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [slideOverTareaId, setSlideOverTareaId] = useState(null);

  useEffect(() => {
    if (!proyecto?.id) return undefined;
    let vivo = true;
    const timer = setTimeout(async () => {
      try {
        const { data: d } = await axiosInstance.get(`/api/proyectos/${proyecto.id}/ruta-critica`);
        if (vivo) { setData(d); setError(null); }
      } catch (err) {
        console.error('Error cargando la red:', err);
        if (vivo) setError('No pudimos calcular la red del proyecto. Recarga la página para intentar de nuevo.');
      }
    }, data ? 300 : 0);
    return () => { vivo = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proyecto?.id, tareas]);

  // ----- Slide-over (mismo patrón de URL que el resto de las vistas) -----

  useEffect(() => {
    const match = location.pathname.match(/\/proyectos\/[^/]+\/red\/tareas\/(\d+)/);
    if (match) {
      const numero = parseInt(match[1], 10);
      const t = (tareas || []).find((x) => x.numero === numero);
      if (t) setSlideOverTareaId(t.id);
    } else {
      setSlideOverTareaId(null);
    }
  }, [tareas, location.pathname]);

  const abrirTarea = useCallback((nodo) => {
    setSlideOverTareaId(nodo.id);
    navigate(`/proyectos/${slug}/red/tareas/${nodo.numero}`);
  }, [navigate, slug]);

  const porCodigo = useMemo(() => {
    const m = new Map();
    (data?.nodos || []).forEach((n) => m.set(n.codigo, n));
    (tareas || []).forEach((t) => { if (!m.has(t.codigo)) m.set(t.codigo, t); });
    return m;
  }, [data, tareas]);

  const abrirPorCodigo = useCallback((codigo) => {
    const n = porCodigo.get(codigo);
    if (n) abrirTarea(n);
  }, [porCodigo, abrirTarea]);

  const cerrarSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    navigate(`/proyectos/${slug}/red`);
  }, [navigate, slug]);

  return (
    <div className="relative h-full flex flex-col bg-white">
      {error && (
        <p className="px-6 py-3 text-sm text-rose-600 border-b border-slate-100">{error}</p>
      )}

      {data ? (
        <>
          <AnalisisPanel
            proyectoId={proyecto?.id}
            resumen={data.resumen}
            cuello={data.cuello_botella}
            onAbrirCodigo={abrirPorCodigo}
          />
          <PertDiagram data={data} onAbrirTarea={abrirTarea} slideOverAbierto={!!slideOverTareaId} />
        </>
      ) : !error && (
        <div className="p-6 space-y-3">
          <div className="h-16 rounded-lg bg-slate-100 animate-pulse" />
          <div className="h-64 rounded-lg bg-slate-50 animate-pulse" />
        </div>
      )}

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={proyecto}
        onClose={cerrarSlideOver}
        onUpdate={(tareaCompleta) => updateTareaLocal?.(tareaCompleta.id, tareaCompleta)}
        onArchive={() => { refetchTareas?.(); cerrarSlideOver(); }}
        onDuplicate={(nueva) => { refetchTareas?.(); setSlideOverTareaId(nueva.id); }}
      />
    </div>
  );
};

export default ProyectoRedView;
